import React, {createContext, useCallback, useContext, useEffect, useMemo, useState, useRef} from 'react';
import {AUTH_SESSION_STORAGE_KEY, AUTH_TOKEN_STORAGE_KEY} from '../../api/config';
import type {RespuestaAutenticacion, RespuestaUsuario, RolUsuario} from '../../api/Objetos';
import {authApi} from '../../api/api';

type SesionAutenticada = {
    token: string;
    tipo: string;
    expiraEnSegundos: number;
    usuario: RespuestaUsuario;
};

type AuthState = {
    sesion: SesionAutenticada | null;
    token: string | null;
    usuario: RespuestaUsuario | null;
    rol: RolUsuario | null;
    isAuthenticated: boolean;
    isAdmin: boolean;
    isLector: boolean;
    setSesion: (value: RespuestaAutenticacion | null) => void;
    logout: () => void;
};

const AuthContext = createContext<AuthState | null>(null);

function readStoredSession(): SesionAutenticada | null {
    try {
        const raw = localStorage.getItem(AUTH_SESSION_STORAGE_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw) as SesionAutenticada;
        if (!parsed?.token || !parsed?.usuario?.rol) return null;
        return parsed;
    } catch {
        return null;
    }
}

function persistSession(next: SesionAutenticada | null) {
    try {
        if (next) {
            localStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(next));
            localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, next.token);
        } else {
            localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
            localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
        }
    } catch {
        // ignore storage failures
    }
}

export function AuthProvider({children}: { children: React.ReactNode }) {
    const [sesion, setSesionState] = useState<SesionAutenticada | null>(() => readStoredSession());
    const heartbeatRef = useRef<number | null>(null);

    const setSesion = useCallback((next: RespuestaAutenticacion | null) => {
        const normalized = next
            ? {
                token: next.token,
                tipo: next.tipo,
                expiraEnSegundos: next.expiraEnSegundos,
                usuario: next.usuario,
            }
            : null;

        setSesionState(normalized);
        persistSession(normalized);
    }, []);

    const logout = useCallback(async () => {
        if (sesion?.token) {
            try {
                await authApi.logout();
            } catch (error) {
                console.error("Error al cerrar sesión en el servidor:", error);
            }
        }
        setSesion(null);
    }, [sesion?.token, setSesion]);

    useEffect(() => {
        const onUnauthorized = () => logout();
        window.addEventListener('auth:no-autorizado', onUnauthorized);
        return () => window.removeEventListener('auth:no-autorizado', onUnauthorized);
    }, [logout]);

    // Lógica del Heartbeat de Sesión
    useEffect(() => {
        if (!sesion?.token) {
            if (heartbeatRef.current) {
                clearInterval(heartbeatRef.current);
                heartbeatRef.current = null;
            }
            return;
        }

        // Ejecutar el heartbeat inmediatamente al iniciar/recargar
        authApi.heartbeat().catch(e => console.warn('Heartbeat inicial falló', e));

        // Programar el heartbeat cada 30 segundos
        heartbeatRef.current = window.setInterval(async () => {
            try {
                await authApi.heartbeat();
            } catch (e: any) {
                // Si el servidor devuelve 401, el interceptor en http.ts ya disparará 'auth:no-autorizado'
                // y el useEffect de arriba llamará a logout() automáticamente.
                console.warn('Fallo en el heartbeat de sesión', e);
            }
        }, 30000);

        return () => {
            if (heartbeatRef.current) {
                clearInterval(heartbeatRef.current);
                heartbeatRef.current = null;
            }
        };
    }, [sesion?.token]);

    const value = useMemo<AuthState>(
        () => ({
            sesion,
            token: sesion?.token ?? null,
            usuario: sesion?.usuario ?? null,
            rol: sesion?.usuario.rol ?? null,
            isAuthenticated: Boolean(sesion?.token),
            isAdmin: sesion?.usuario.rol === 'ADMIN',
            isLector: sesion?.usuario.rol === 'LECTOR',
            setSesion,
            logout,
        }),
        [logout, sesion, setSesion]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
    return ctx;
}
